/*
 * App Actions
 *
 * Actions change things in your application
 * Since this boilerplate uses a uni-directional data flow, specifically redux,
 * we have these actions which are the only way your application interacts with
 * your application state. This guarantees that your state is up to date and nobody
 * messes it up weirdly somewhere.
 *
 * To add a new Action:
 * 1) Import your constant
 * 2) Add a function like this:
 *    export function yourAction(var) {
 *        return { type: YOUR_ACTION_CONSTANT, var: var }
 *    }
 */
import { checkIsOnlyStandardUserRole } from 'commonResources/utils';
import { Dispatch } from 'react';
import { AnyAction } from 'redux';
import {
  ON_PRODUCT_MENU_SELECT,
  ON_AVATAR_MENU_SELECT,
  ON_MULTI_GROUP_OPEN,
  ON_MULTI_GROUP_CHECK,
  ON_SINGLE_GROUP_OPEN,
  ON_SINGLE_GROUP_SELECT,
  ON_SINGLE_SELECT_INQUIRIES,
  ON_SINGLE_SELECT_MY_RECENT_ACTIVITIES,
  ON_SINGLE_SELECT_RULES,
  ON_SINGLE_SELECT_REASON_CODES,
  ON_SINGLE_SELECT_DOCUMENT_REFERENCES,
  ON_SINGLE_SELECT_CLIENTS,
  ON_SINGLE_SELECT_ACTIVITY_LOG,
  ON_SINGLE_SELECT_CREATE_INVOICE,
  ON_SINGLE_SELECT_INVOICE_SEARCH,
  ON_SINGLE_GROUP_SELECT_INVOICING,
  USER_SET,
  TOGGLE_AUTH_MODAL_OPEN,
  ON_INITIAL_NOTIFICATIONS,
  ON_UPDATE_NOTIFICATIONS,
  ON_UNPROCESSED_NOTIFICATIONS,
  MARK_NOTIFICATIONS_READ,
  TOGGLE_HASE_USER_ACKNOWLEDGED,
  SET_NETWORK_ERROR_NOTIFICATION,
  SET_NOTIFICATION_VISIBLE,
  SET_OPEN_ANIMATION,
  SET_CLOSE_ANIMATION,
  SET_NAV_MENU_ITEMS,
  NAV_MENU_ITEMS,
  SET_ASSIGNEE_LIST,
  SET_APPEAL_STATUSES,
  SET_USER_CONFIGS,
  TOGGLE_USER_COLUMN_CONFIG_LOADING,
  ON_SINGLE_SELECT_APPEAL_RESPONSES,
  NAV_MENU_PERMISSIONS,
  TOGGLE_ASSIGNEE_LIST_LOADING,
} from './constants';
import { notificationMessages, notificationTypes } from 'constants/commonConstants';
import {
  getAppealCountData,
  getAppealStatusesAll,
  getAppealStatusesCountAll,
  getAssigneeListData,
  requestGetUserConfigs,
} from './api';
import { AssigneeData, UserConfig } from './types';
import { AppealStatus } from 'containers/Appeals/modules/types';
import { resetAppealResponses } from 'containers/AppealResponses/modules/actions';
export const onProductMenuSelect = (state) => ({
  type: ON_PRODUCT_MENU_SELECT,
  state,
});

export const onAvatarMenuSelect = (state) => ({
  type: ON_AVATAR_MENU_SELECT,
  state,
});

export const onSingleGroupOpen = (state) => ({
  type: ON_SINGLE_GROUP_OPEN,
  state,
});

export const onSingleGroupSelect = (state) => ({
  type: ON_SINGLE_GROUP_SELECT,
  state,
});

export const onSingleSelectInquiries = () => ({
  type: ON_SINGLE_SELECT_INQUIRIES,
});

export const onSingleSelectMyRecentActivities = () => ({
  type: ON_SINGLE_SELECT_MY_RECENT_ACTIVITIES,
});

export const onSingleSelectRules = () => ({
  type: ON_SINGLE_SELECT_RULES,
});

export const onSingleSelectReasonCodes = () => ({
  type: ON_SINGLE_SELECT_REASON_CODES,
});
export const onSingleSelectDocumentReferences = () => ({
  type: ON_SINGLE_SELECT_DOCUMENT_REFERENCES,
});
export const onSingleSelectClients = () => ({
  type: ON_SINGLE_SELECT_CLIENTS,
});

export const onSingleSelectAppealResponses = () => ({
  type: ON_SINGLE_SELECT_APPEAL_RESPONSES,
});

export const onSingleSelectActivityLog = () => ({
  type: ON_SINGLE_SELECT_ACTIVITY_LOG,
});

export const onSingleSelectCreateInvoice = () => ({
  type: ON_SINGLE_SELECT_CREATE_INVOICE,
});

export const onSingleSelectInvoiceSearch = () => ({
  type: ON_SINGLE_SELECT_INVOICE_SEARCH,
});

export const onSingleGroupSelectInvoicing = (state) => ({
  type: ON_SINGLE_GROUP_SELECT_INVOICING,
  state,
});

export const onMultiGroupOpen = (state) => ({
  type: ON_MULTI_GROUP_OPEN,
  state,
});

export const onMultiGroupCheck = (state) => ({
  type: ON_MULTI_GROUP_CHECK,
  state,
});

export const setUser = (state) => ({
  type: USER_SET,
  state,
});

export const getUserConfigs = (cfgName: string) => async (dispatch: Dispatch<any>) => {
  try {
    const response = await requestGetUserConfigs(cfgName);
    dispatch(toggleUserColumnConfigLoading(true));
    dispatch(setUserConfigs(response));
    if (!response) {
      dispatch(
        setNotificationBanner(
          true,
          notificationMessages.networkErrorNotification,
          notificationTypes.negative,
        ),
      );
    }
  } catch (error) {
    dispatch(
      setNotificationBanner(
        true,
        notificationMessages.networkErrorNotification,
        notificationTypes.negative,
      ),
    );
  } finally {
    dispatch(toggleUserColumnConfigLoading(false));
  }
};
export const getAssigneeList =
  (userId: number = 0) =>
  async (dispatch: Dispatch<any>) => {
    try {
      dispatch(toggleAssigneeListLoading(true));
      //default to true always for now
      const isActiveOnly = false;
      const response = await getAssigneeListData(userId, isActiveOnly);
      dispatch(setAssigneeList(response));
    } catch (error) {
      dispatch(
        setNotificationBanner(
          true,
          notificationMessages.networkErrorNotification,
          notificationTypes.negative,
        ),
      );
    } finally {
      dispatch(toggleAssigneeListLoading(false));
    }
  };

export const getAppealStatuses = () => async (dispatch: Dispatch<any>) => {
  try {
    const response: AppealStatus[] = await getAppealStatusesAll();

    const responseCount = await getAppealStatusesCountAll();

    if (!response || !responseCount) {
      dispatch(
        setNotificationBanner(
          true,
          notificationMessages.networkErrorNotification,
          notificationTypes.negative,
        ),
      );
    }

    const transformedResponseCount = Object.entries(responseCount || {}).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key.replace('Count', '').toLowerCase()]: value as number,
      }),
      {} as Record<string, number>,
    );

    const updatedResponse = [
      ...(response || []),
      {
        appealStatusID: -1,
        description: 'New',
        count: 0,
        hidden: true,
      },
    ].map((item) => ({
      ...item,
      count: transformedResponseCount[item.description.toLowerCase()] || 0,
    }));

    dispatch(setAppealStatuses(updatedResponse));
  } catch (error) {
    dispatch(
      setNotificationBanner(
        true,
        notificationMessages.networkErrorNotification,
        notificationTypes.negative,
      ),
    );
  }
};

export const setAssigneeList = (state: AssigneeData) => ({
  type: SET_ASSIGNEE_LIST,
  state,
});
export const setUserConfigs = (state: UserConfig[]) => ({
  type: SET_USER_CONFIGS,
  state,
});
export const setAppealStatuses = (data: AppealStatus[]) => ({
  type: SET_APPEAL_STATUSES,
  data,
});
export const setNavMenuItems = (user) => {
  let combinedPermissions: any[] = [];
  for (let i = 0; i < user.permissions.length; i++) {
    combinedPermissions = [
      ...new Set([...combinedPermissions, ...user.permissions[i].permissions]),
    ];
  }
  const permissionNavMenuItems = NAV_MENU_ITEMS.filter((item) =>
    combinedPermissions.includes(NAV_MENU_PERMISSIONS[item.label]),
  );
  return {
    type: SET_NAV_MENU_ITEMS,
    state: permissionNavMenuItems,
  };
};
export const toggelHasUserAcknowledged = (state) => ({
  type: TOGGLE_HASE_USER_ACKNOWLEDGED,
  state,
});

export const toggleAuthModalOpen = (state) => ({
  type: TOGGLE_AUTH_MODAL_OPEN,
  state,
});

export const toggleUserColumnConfigLoading = (state) => ({
  type: TOGGLE_USER_COLUMN_CONFIG_LOADING,
  state,
});
export const toggleAssigneeListLoading = (state) => ({
  type: TOGGLE_ASSIGNEE_LIST_LOADING,
  state,
});
// NOTIFICATIONS
// ––––––––––––––––––––––––––––––––––––––––––––––––––––––
export const onInitialNotifications = (state) => ({
  type: ON_INITIAL_NOTIFICATIONS,
  state,
});

export const onUpdateNotifications = (state) => ({
  type: ON_UPDATE_NOTIFICATIONS,
  state,
});

export const onUnprocessedNotifications = (state) => ({
  type: ON_UNPROCESSED_NOTIFICATIONS,
  state,
});

export const onMarkNotificationsRead = (state) => ({
  type: MARK_NOTIFICATIONS_READ,
  state,
});
export const setNotificationBanner =
  (notificationVisible, notificationMessage = '', notificationType, animationTime = 300) =>
  (dispatch: Dispatch<AnyAction>) => {
    if (notificationVisible) {
      dispatch({
        type: SET_NOTIFICATION_VISIBLE,
        notificationVisible: true,
        notificationMessage,
        notificationType,
      });
      dispatch({
        type: SET_OPEN_ANIMATION,
        openAnimationActive: true,
      });
      setTimeout(() => {
        dispatch({
          type: SET_OPEN_ANIMATION,
          openAnimationActive: false,
        });
      }, animationTime);
    } else {
      dispatch({
        type: SET_NOTIFICATION_VISIBLE,
        notificationVisible: false,
        notificationMessage: '',
      });
      dispatch({
        type: SET_CLOSE_ANIMATION,
        closeAnimationActive: true,
      });
      setTimeout(() => {
        dispatch({
          type: SET_CLOSE_ANIMATION,
          closeAnimationActive: false,
        });
      }, animationTime);
    }
  };
